import styled from "styled-components";
import { btcToString } from "./utils";

const TAPE_LENGTH = 90;

const Wrapper = styled.div`
  margin: 0.5rem 0 1rem 0;
`;

const Caption = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.2rem 1rem;
  font-size: 0.7rem;
  color: #a3abba;
  margin-bottom: 0.2rem;
`;

const Key = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    background-color: #32a1ce;
  }
`;

const Tape = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1px;
  height: 56px;
  border-bottom: 1px solid #3f3f44;
  overflow: hidden;

  @media (max-width: 700px) {
    height: 44px;
  }
`;

const Tick = styled.div`
  flex: 1 1 0;
  min-width: 2px;
  height: ${({ $pct }) => $pct}%;
  background-color: ${({ $tagged }) => ($tagged ? "#32a1ce" : "#FF9900")};
  opacity: ${({ $tagged }) => ($tagged ? 1 : 0.55)};

  &:hover {
    opacity: 1;
  }
`;

// Values span many orders of magnitude - dust payments next to whale moves - so
// a linear height would render almost everything as a flat line. Height is the
// log of the value in sats, normalised against 1000 BTC.
const heightPct = (btc) => {
  const sats = Math.max(btc, 0) * 1e8;
  const pct = (Math.log10(sats + 1) / Math.log10(1e11)) * 100;
  return Math.max(4, Math.min(100, pct));
};

const TransactionTape = ({ transactions }) => {
  // Transfers only. About 91% of unconfirmed transactions move no value at all -
  // measured over 1,000 of them live - because a single input returning to
  // itself alongside a zero-value OP_RETURN is how data gets written to the
  // chain, and that is most of the traffic right now. Drawn by value moved they
  // are all zero, and the tape went to eighty-nine flat stubs and one bar.
  //
  // Filtering them out rather than drawing them at their output total, which is
  // what this used to do: the height means value moved everywhere else on the
  // page and a bar that means something different here would be the same
  // overstatement in a new place. The buffer holds 2,000 transactions and about
  // 182 of them are transfers, so 90 bars still fill from roughly the last
  // couple of minutes.
  //
  // The table below narrows the same way and for the same reason, though it is a
  // toggle there rather than a rule: a row reading "0.00000000 BTC" is legible
  // where a bar of no height is not, so showing them is a choice worth offering.
  // Both say transfer rather than transaction when they are narrowed.
  const recent = transactions
    .filter((tx) => tx.netMovedBtc > 0)
    .slice(0, TAPE_LENGTH)
    .reverse();
  const largest = recent.reduce(
    (max, tx) => (tx.netMovedBtc > max ? tx.netMovedBtc : max),
    0
  );

  return (
    <Wrapper>
      <Caption>
        <span>Each bar is one transfer, newest on the right</span>
        <Key>known wallet</Key>
        {/* before the first transfer arrives there is no largest, and
            "largest shown: 0.00000000 BTC" reads as a broken number rather
            than an empty one */}
        {recent.length > 0 && (
          <span>largest shown: {btcToString(largest)} BTC</span>
        )}
      </Caption>
      <Tape>
        {recent.map((tx) => (
          <Tick
            key={tx.hash}
            $pct={heightPct(tx.netMovedBtc)}
            $tagged={tx.tags && tx.tags.length > 0}
            title={`${btcToString(tx.netMovedBtc)} BTC${
              tx.tags && tx.tags.length ? ` — ${tx.tags[0].label}` : ""
            }`}
          />
        ))}
      </Tape>
    </Wrapper>
  );
};

export default TransactionTape;
